/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* eslint-disable no-unused-vars */
import PaymentFailureNotification from '@cchome/home-components/src/components/PaymentFailureNotification';
import Provider from '@cchome/home-components/src/provider/Provider';
import Wait from '@react/react-spectrum/Wait';
import {
	campaignId,
	CCHomeAppsAppPickerUtil,
	CCHomeAppsUtil,
	CCInstallationView,
	setCampaignVariationMapWithFGTests,
} from 'cchome-apps';
import {DeviceDetectionUtil} from 'cchome-shared/utils';
import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {matchRoutes, renderRoutes} from 'react-router-config';
import AnalyticsUtil from '../../../utils/AnalyticsUtil';
import {getEnvironment} from '../../../utils/Environment';
import Url from '../../../utils/Url';
import {setAnalyticsOptinInited} from '../../actions/appActions';
import {resetProfileFetchError} from '../../actions/profileActions';
import CCBanner from '../../components/Banner/CCBanner';
import {error} from '../../components/ErrorToast/ErrorToast';
import FeedBackSurvey from '../../components/FeedBackSurvey/FeedBackSurvey';
import Footer from '../../components/Footer/Footer';
import requireAuth from '../../components/higherOrderComponent/requireAuth';
import {APPS_TAB, DISCOVER_TAB_LEARN_TUTORIAL} from '../../components/LeftNav/constants';
import LeftNav from '../../components/LeftNav/LeftNav';
import Navbar from '../../components/NavBar/Navbar';
import AppsUtils from '../../containers/Apps/AppsUtils';
import SearchHostContext from '../../containers/Search/SearchHostContext';
import {AppHomeRoutes} from '../../Routes/AppHomeRoutes';
import {AppsRoutes} from '../../Routes/AppsRoutes';
import {DiscoverRoutes} from '../../Routes/DiscoverRoutes';
import {ErrorRoutes} from '../../Routes/ErrorRoutes';
import {HomeRoutes} from '../../Routes/HomeRoutes';
import {LearnRoutes} from '../../Routes/LearnRoutes';
import {SearchRoutes} from '../../Routes/SearchRoutes';
import GrowthUtil from '../Growth/growthUtil';
/* eslint-enable no-unused-vars */
import './App.scss';

interface Props {
	app: app;
	profile: any;
	route: any;
	location: Location;
	history: any;
	children: Object;
	intl: any;
	planCode: string;
	forceHideMenu: boolean;
	forceHideNavbar: boolean;
	mainContainerOverlayEnabled: boolean;
	profileFetchError: boolean;
	resetProfileFetchError: Function;
	// profileFetchFinished: boolean;
	gqlProfile: Object;
	overwriteComponent: Function;
	language: string;
	setAnalyticsOptinInited: Function;
	userType: string;
}

interface State {
	overwriteComponent: any;
	previousPath: string;
	hasNotification: boolean;
	showedProfileServiceError: boolean;
}

const PATHS = {
	HOME: 'home',
	SEARCH: 'search',
	DISCOVER: 'discover',
	APPS: 'apps',
	LEARN: 'learn',
	LEARNTUTORIAL: 'learnTutorial',
	ERROR: 'error',
	WEBAPPS: 'webapps',
	UPLOAD: 'upload',
};

class App extends Component<Props, State> {
	isComponentMounted: boolean;

	constructor(props) {
		super(props);
		this.isComponentMounted = false;
		this.state = {
			overwriteComponent: null,
			previousPath: '', // home, apps, learn
			hasNotification: false,
			showedProfileServiceError: false,
		};

		this.setSearchHostContextUIState();

		if (window.cchome.util.optOutAnalyticsInited) {
			this.onSendAnalyticsInited();
		}

		window.addEventListener('analyticsOptinInited', this.onSendAnalyticsInited.bind(this));

		// Redirect LCCP users to lightroom single app plan page
		if (
			CCHomeAppsUtil.getPathData().mainAppsTab === 'starter'
			&& ['lccp', 'ccpp', 'ccppn'].includes(CCHomeAppsUtil.getPathData().product)
		) {
			switch (CCHomeAppsUtil.getPathData().product) {
				case 'lccp':
					this.props.history.replace('/apps/starter/lightroom');
					break;
				case 'ccpp':
					this.props.history.replace('/apps/starter/photo-20gb');
					break;
				case 'ccppn':
					this.props.history.replace('/apps/starter/photo-1tb');
					break;
			}
		}

		if (window.cchome.util.hasFeature('temp_cch_8203_free')) {
			// Set Trial to TWP variation ID in TestUtil list so that it can be used later
			setCampaignVariationMapWithFGTests(
				campaignId.trialToTWPFree,
				window.cchome.util.getExperimentVariation(
					'temp_bypass_trial_to_twp_split',
					'temp_cch_8203_twp_trial_jdi',
				),
			);
		}
		if (window.cchome.util.hasFeature('temp_cch_8203_paid')) {
			// Set Trial to TWP variation ID in TestUtil list so that it can be used later
			setCampaignVariationMapWithFGTests(
				campaignId.trialToTWPPaid,
				window.cchome.util.getExperimentVariation(
					'temp_bypass_trial_to_twp_split',
					'temp_cch_8203_twp_trial_test',
				),
			);
		}

		if (window.cchome.util.hasFeature('temp_cch_7467')) {
			// Set App Picker Intent variation id in TestUtil so that it can be used later
			setCampaignVariationMapWithFGTests(
				campaignId.appPickerIntent,
				window.cchome.util.getExperimentVariation('temp_bypass_app_picker_intent_split', 'temp_app_picker_intent'),
			);
		}

		if (window.cchome.util.hasFeature('temp_cch_12205')) {
			// Set CME-1 Experiment variation ID in TestUtil list so that it can be used later
			setCampaignVariationMapWithFGTests(
				campaignId.cme1Experiment,
				window.cchome.util.getExperimentVariation('temp_bypass_cch_12205_split', 'temp_cch_12205_experiment'),
			);
		}
	}

	/**
	 * Called when we get the return value from the PIIP service.
	 */
	onSendAnalyticsInited() {
		this.props.setAnalyticsOptinInited(true);
	}

	isAppSubRouteChanged(prevProps) {
		if (this.props.location && prevProps.location && this.props.location.pathname && prevProps.location.pathname) {
			if (
				this.props.location.pathname.split('/')[1] === prevProps.location.pathname.split('/')[1]
				&& this.props.location.pathname.split('/')[1] === 'apps'
				&& typeof this.props.location.pathname.split('/')[2] !== 'undefined'
			) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Check if the current path is matching the varible PATHS passing in
	 */
	isPath(path: string) {
		const currentPath = this.props && this.props.location && this.props.location.pathname;
		const match = matchRoutes(this.props.route.routes, currentPath);

		let routes: Array<any> = [];

		switch (path) {
			case PATHS.HOME:
				routes = HomeRoutes;
				break;

			case PATHS.SEARCH:
				routes = SearchRoutes;
				break;

			case PATHS.DISCOVER:
				routes = DiscoverRoutes;
				break;

			case PATHS.APPS:
				routes = AppsRoutes;
				break;

			case PATHS.LEARN:
				routes = LearnRoutes;
				break;

			case PATHS.LEARNTUTORIAL:
				routes = [
					{path: '/learn/:app/:level/:field/:playlist/:tutorial'},
					{path: '/learn/:app/:level/:field/:playlist/'},
				];
				break;

			case PATHS.WEBAPPS:
				routes = AppHomeRoutes;
				break;

			case PATHS.ERROR:
				routes = ErrorRoutes;
				break;
		}
		return routes.some(route => route.path === match[0].match.path);
	}

	componentDidMount() {
		this.isComponentMounted = true;
		this.updateDocumentTitle();
		this.checkDeprecatedBrowser();
		this.setSearchHostContextUIState();

		// Temp for QE to test
		if (window.cchome.util.hasFeature('test_addMiniPlanModal')) {
			const addMiniPlanModal = require('../../components/MiniPlanModal/MiniPlanModal').addMiniPlanModal;
			window.cchome.test = {
				addMiniPlanModal: addMiniPlanModal.bind(this),
			};
		}
	}

	/* istanbul ignore next */
	componentDidUpdate(prevProps) {
		const presentPath
			= this.props.location && this.props.location.pathname && this.props.location.pathname.split('/');
		if (presentPath && presentPath[1] !== 'apps') {
			this.updateDocumentTitle();
		}

		if (this.props.location !== prevProps.location) {
			AnalyticsUtil.setReferrer(prevProps.location.pathname);
			if (!this.isAppSubRouteChanged(prevProps)) {
				window.scrollTo(0, 0);
			}
		}

		// We want this here b/c on each state change -- ie. tab change -- we need to capture that
		this.setSearchHostContextUIState();

		this.showProfileFetchError();
	}

	componentWillUnmount() {
		this.isComponentMounted = false;
		window.removeEventListener('analyticsOptinInited', this.onSendAnalyticsInited.bind(this));
	}

	checkDeprecatedBrowser() {
		/* istanbul ignore else */
		if (window.cchome.util.isDeprecatedBrowser()) {
			error(this.props.intl.formatMessage({id: 'app.deprecated.browser.error'}), {timeout: 9999999});
		}
	}

	showProfileFetchError() {
		if (!this.state.showedProfileServiceError && this.props.profileFetchError && !this.isAppStarter()) {
			if (window.cchome.util.hasFeature('20200206_errorToastMessage')) {
				error(this.props.intl.formatMessage({id: 'app.profile.fetch.error.toast.new'}));
			}
			else {
				error(this.props.intl.formatMessage({id: 'app.profile.fetch.error.toast'}));
			}
			this.setState({showedProfileServiceError: true});
		}
		// this.props.resetProfileFetchError();
	}

	updateDocumentTitle() {
		let titleId = '';

		const matches = matchRoutes(this.props.route.routes, this.props.location.pathname);

		/* istanbul ignore else */
		if (matches && matches.length > 0) {
			titleId = matches[0].route.titleId;
		}

		if (!titleId || titleId === '') {
			titleId = 'app.document.title';
		}

		window.cchome.util.setDocumentTitle(this.props.intl.formatMessage({id: titleId}));
	}

	getMainContainerStyle() {
		let className = 'main-container';

		if (this.props.forceHideMenu) {
			className = 'main-container fullscreen';
		}
		if (this.props.forceHideNavbar) {
			className = 'main-container one-up-fullscreen';
		}
		if (this.checkUserIsAllowedForFeedbackSurvey()) {
			className += ' feedback-inline';
		}

		if (this.state.overwriteComponent === null) {
			className += ' top-nav';
		}

		if (this.isPath(PATHS.HOME)) {
			className += ' v3';
		}
		return className;
	}

	isAppStarter() {
		const matches = matchRoutes(this.props.route.routes, this.props.location.pathname);
		if (matches && matches.length > 0 && matches[0].route.tab === APPS_TAB) {
			return true;
		}
		return false;
	}

	isAllAppsStarter() {
		const matches = matchRoutes(this.props.route.routes, this.props.location.pathname);

		/* istanbul ignore else */
		if (matches && matches.length > 0 && matches[0].route.tab === APPS_TAB) {
			const path = this.props.location.pathname.split('/');

			/* istanbul ignore else */
			if (
				(path[2] === 'starter' && path[3] === 'all-apps')
				|| (Url.getParam('isStarter') && path[3] === 'photoshop' && path[4] === 'installation')
			) {
				return true;
			}
		}
		return false;
	}

	isNUJTestWorkflow() {
		// Used for cchome-react related changes (like changes in util nav, top nav, get help widget) if this is NUJ experience
		return CCHomeAppsAppPickerUtil.isNUJAppPickerExperience() || CCHomeAppsAppPickerUtil.isSingleAppPlanNUJExp();
	}

	addGrowthClass() {
		if (GrowthUtil.isGrowthWorkflow()) {
			const growthCongfig = GrowthUtil.getGrowthConfig();
			if (growthCongfig) {
				return ` ${growthCongfig.appContainerClass}`;
			}
		}
		return '';
	}

	showCCInstallationDialog() {
		if (GrowthUtil.hideCCInstallationDialog()) {
			return false;
		}

		return true;
	}

	isCCPPOrAllApps() {
		return this.isAllAppsStarter();
	}

	isMobilePhotoPlan() {
		const matches = matchRoutes(this.props.route.routes, this.props.location.pathname);
		if (matches && matches.length > 0 && matches[0].route.tab === APPS_TAB) {
			const path = window.location.pathname.split('/');

			/* istanbul ignore else */
			return (
				DeviceDetectionUtil.isMobileOrTab()
				&& DeviceDetectionUtil.isIPad()
				&& path[2] === 'starter'
				&& (path[3] === 'photo-20gb' || path[3] === 'photo-1tb')
			);
			// use in case of app picker for Desktop
			// (Url.getParam('isAppPicker') && (path[3] === 'lightroom' || path[3] === 'lightroom-classic' || path[3] === 'photoshop') && path[4] === 'installation'))
		}
		return false;
	}

	checkUserIsAllowedForFeedbackSurvey() {
		let isUserAllowed = false; // By default, assume the user is not allowed.
		const isHomePage = this.isPath(PATHS.HOME);
		const isEnterpriseOrTeam
			= this.props.planCode
			&& (this.props.planCode.indexOf('enterprise') >= 0 || this.props.planCode.indexOf('team') >= 0);
		const isEnglish = this.props.app.language === 'en';
		const featureFlag = window.cchome.util.hasFeature('Inline_Feedback');

		/* istanbul ignore else */
		// planCode could be undefined, so adding another check for planCode to make sure it is valid before assuming user is Enterprise or Team
		if (featureFlag && this.props.planCode && !isEnterpriseOrTeam && isEnglish && isHomePage) {
			const userID = window.cchome.util.getUserId();
			const userSubmittedFeedback
				= window.cchome.util.storageAvailable('localStorage')
				&& localStorage.getItem(`submittedFeedback0819-${userID}`) === 'true';

			/* istanbul ignore else */
			if (!userSubmittedFeedback) {
				isUserAllowed = true;
			}
		}
		return isUserAllowed;
	}

	/**
	 * Sets the current tab of the host CCWeb inside the HostContext state.
	 */
	setSearchHostContextUIState() {
		const searchHostContext = new SearchHostContext();

		if (this.state.previousPath === '' && this.isPath(PATHS.SEARCH)) {
			// Case when user drops immediately into a search page -- ie. copy-paste link
			searchHostContext.setHostUIState(PATHS.HOME, '', '');
		}
		else if (!this.isPath(PATHS.SEARCH) && this.isComponentMounted) {
			if (this.isPath(PATHS.LEARN) && this.state.previousPath !== PATHS.LEARN) {
				searchHostContext.setHostUIState(PATHS.LEARN, '', '');
				this.setState({previousPath: PATHS.LEARN});
			}
			else if (this.isPath(PATHS.APPS) && this.state.previousPath !== PATHS.APPS) {
				searchHostContext.setHostUIState(PATHS.APPS, '', '');
				this.setState({previousPath: PATHS.APPS});
			}
			else if (this.isPath(PATHS.HOME) && this.state.previousPath !== PATHS.HOME) {
				searchHostContext.setHostUIState(PATHS.HOME, '', '');
				this.setState({previousPath: PATHS.HOME});
			}
		}
	}

	/**
	 * Renders the Feedback survey component
	 *
	 */
	renderFeedbackSurvey() {
		let jsxArr: JSX.Element | null = null;

		/* istanbul ignore else */
		if (this.checkUserIsAllowedForFeedbackSurvey()) {
			jsxArr = <FeedBackSurvey />;
		}
		return jsxArr;
	}

	setOverwriteComponentForRenderRoute = (component: any) => {
		this.setState({overwriteComponent: component});
	};

	/**
	 * Renders PaymentFailureNotification component
	 *
	 */
	renderNotification() {
		const featureFlagsEnabled = window.cchome.util.hasFeature('home_components_notification');
		const allowedPath
			= (this.isPath(PATHS.HOME)
				|| this.isPath(PATHS.LEARN)
				|| this.isPath(PATHS.APPS)
				|| this.isPath(PATHS.WEBAPPS))
			&& !this.isPath(PATHS.LEARNTUTORIAL);
		const excludedPath = GrowthUtil.isGrowthWorkflow() || this.isNUJTestWorkflow() || CCHomeAppsUtil.isCCHInLMS();

		const setHasNotification = (show: boolean) => {
			this.setState({hasNotification: show});
		};

		if (featureFlagsEnabled && allowedPath && !excludedPath) {
			return (
				<div className='notification-module-adjustment'>
					<PaymentFailureNotification onShow={setHasNotification} />
				</div>
			);
		}

		return null;
	}

	render() {
		const env = getEnvironment();
		const lang = this.props.app.language || 'en';
		const filteredRoutes = this.props.route.routes;
		const {profile, history} = this.props;

		/* istanbul ignore else */
		if (document.documentElement) {
			document.documentElement.lang = lang;
		}

		/* set browser type */
		if (document.documentElement) {
			document.documentElement.setAttribute('data-browser', window.cchome.util.getBrowserType());
		}
		if (document.documentElement) {
			document.documentElement.setAttribute(
				'data-device',
				DeviceDetectionUtil.isMobileOrTab() ? 'mobile-or-tab' : 'desktop',
			);
		}
		if (!this.props.planCode && Object.keys(this.props.gqlProfile).length === 0 && !this.props.profileFetchError) {
			// You have to wait for gqlProfile to be set b/c other components in CCHome
			// rely on the raw profile -- not just the clean profile that's used in planCode
			// Wait if the planCode is not ready and there is no error from profile service
			// If there is profile fetch error, individual page should handle the error themselves
			return (
				<div key='transitionPage' className={'transition-screen-wrapper'}>
					<div className='transition-screen-container'>
						<Wait className='spinner' centered size='L' />
					</div>
				</div>
			);
		}

		if (this.isPath(PATHS.ERROR)) {
			return (
				<div className='app-container'>
					<Navbar
						// @ts-ignore
						locale={lang}
						isAllAppsStarter={this.isAllAppsStarter()}
						isMobilePhotoPlan={this.isMobilePhotoPlan()}
						isNUJTestWorkflow={this.isNUJTestWorkflow()}
						environment={env}
						history={history}
					/>
					<div className='main-container-error-state'>
						<div className='center-container'>{renderRoutes(filteredRoutes, {locale: lang, env})}</div>
					</div>
				</div>
			);
		}
		if (this.isPath(PATHS.SEARCH)) {
			return (
				<div className={`app-container top-nav${GrowthUtil.isGrowthWorkflow() ? this.addGrowthClass() : ''}`}>
					<Navbar
						// @ts-ignore
						locale={lang}
						isAllAppsStarter={this.isAllAppsStarter()}
						isMobilePhotoPlan={this.isMobilePhotoPlan()}
						routes={this.props.route.routes}
						environment={env}
						isNUJTestWorkflow={this.isNUJTestWorkflow()}
						history={history}
					/>
					<div className='main-container-search-page'>
						<div className='center-container'>{renderRoutes(filteredRoutes, {locale: lang, env})}</div>
					</div>
					<Footer
						locale={lang}
						isAllAppsStarter={this.isAllAppsStarter()}
						isMobilePhotoPlan={this.isMobilePhotoPlan()}
						isNUJTestWorkflow={this.isNUJTestWorkflow()}
					/>
					{(lang === 'en' || window.cchome.util.hasFeature('temp_enable_ccinstall_toast_all')) && (
						<CCInstallationView
							profile={profile}
							imsData={AppsUtils.getIMSData()}
							environment={env}
							locale={lang}
						/>
					)}
				</div>
			);
		}
		if (this.isPath(PATHS.DISCOVER)) {
			const match = matchRoutes(this.props.route.routes, this.props.location.pathname);

			// if FF is enabled, we don't hide discover for k12 users anymore
			if (
				!window.cchome.util.hasFeature('enable_discover_k12')
				&& this.props.planCode
				&& this.props.planCode.toLowerCase().includes('k12')
				&& match[0].route.subTab !== DISCOVER_TAB_LEARN_TUTORIAL
			) {
				// in case enterprise user enters discover page directly by url
				this.props.history.push('/');
			}

			let discoverFullScreen = false;
			if ((match.length > 0 && match[0].route.subTab) || DeviceDetectionUtil.isInternetExplorer()) {
				discoverFullScreen = true;
			}

			return (
				<div className={'app-container top-nav'}>
					<Navbar
						// @ts-ignore
						locale={lang}
						isAllAppsStarter={this.isAllAppsStarter()}
						isMobilePhotoPlan={this.isMobilePhotoPlan()}
						routes={this.props.route.routes}
						environment={env}
						isNUJTestWorkflow={this.isNUJTestWorkflow()}
						history={history}
					/>
					<LeftNav
						overwriteComponent={this.state.overwriteComponent}
						routes={this.props.route.routes}
						pathName={this.props.location.pathname}
					/>
					<div
						className={
							discoverFullScreen ? 'main-container-discover-page-learn' : 'main-container-discover-page'
						}
					>
						<div className='center-container'>
							{renderRoutes(filteredRoutes, {
								locale: lang,
								env,
								setOverwriteComponent: this.setOverwriteComponentForRenderRoute,
							})}
						</div>
					</div>
					<Footer
						locale={lang}
						isAllAppsStarter={this.isAllAppsStarter()}
						isMobilePhotoPlan={this.isMobilePhotoPlan()}
						isNUJTestWorkflow={this.isNUJTestWorkflow()}
					/>
					{(lang === 'en' || window.cchome.util.hasFeature('temp_enable_ccinstall_toast_all')) && (
						<CCInstallationView
							profile={profile}
							imsData={AppsUtils.getIMSData()}
							environment={env}
							locale={lang}
						/>
					)}
				</div>
			);
		}

		const appContainerClass = () => {
			let appContainerClass = 'app-container top-nav';

			if (this.isCCPPOrAllApps() || this.isMobilePhotoPlan()) {
				appContainerClass += ' hide-leftMenu';
			}
			if (this.isAppStarter()) {
				appContainerClass += ' cchome-apps';
			}
			if (GrowthUtil.isGrowthWorkflow()) {
				appContainerClass += this.addGrowthClass();
			}
			if (CCHomeAppsUtil.isCCHInLMS()) {
				appContainerClass += ' adjust-cchome-in-canvas';
			}
			if (this.state.hasNotification) {
				appContainerClass += ' notification-enabled';
			}
			if (
				window.cchome.util.isMobileOrTabletBrowser()
				&& !(localStorage && localStorage.getItem('dismissedCCMobileBanner'))
			) {
				appContainerClass += ' with-top-banner';
			}
			if (this.isPath(PATHS.HOME)) {
				appContainerClass += ' v3';
			}

			return appContainerClass;
		};

		const lifelineEligible = () => {
			if (
				window.cchome.util.hasFeature('lifelineCCD')
				&& !DeviceDetectionUtil.isMobileOrTab()
				&& this.props.userType !== 'unknown'
			) {
				return true;
			}
			return false;
		};

		const {gqlProfile} = profile;

		const homeComponentsIMSProfile = {
			...window.adobeIMS.getUserProfile(),
			accessToken: window.cchome.util.getAccessToken(),
		};

		return (
			<div className={appContainerClass()}>
				{this.isPath(PATHS.HOME) && <CCBanner />}
				<Navbar
					// @ts-ignore
					locale={lang}
					isAllAppsStarter={this.isAllAppsStarter()}
					routes={this.props.route.routes}
					isMobilePhotoPlan={this.isMobilePhotoPlan()}
					environment={env}
					isNUJTestWorkflow={this.isNUJTestWorkflow()}
					history={history}
				/>
				<Provider
					imsData={homeComponentsIMSProfile}
					env={getEnvironment()}
					logger={window.cchome.util.logger}
					featureFlags={window.cchome.util.getAllFeatures()}
				>
					{this.renderNotification()}
					{!this.isCCPPOrAllApps() && !this.isMobilePhotoPlan() && (
						<LeftNav
							overwriteComponent={this.state.overwriteComponent}
							routes={this.props.route.routes}
							pathName={this.props.location.pathname}
						/>
					)}
					<div className={this.getMainContainerStyle()}>
						<div className='center-container'>
							{renderRoutes(filteredRoutes, {
								locale: lang,
								env,
								setOverwriteComponent: this.setOverwriteComponentForRenderRoute,
								isAllAppsStarter: this.isCCPPOrAllApps() || this.isMobilePhotoPlan(),
								isNUJTestWorkflow: this.isNUJTestWorkflow(),
								paidSubscriptionsInfo:
									(gqlProfile
										&& gqlProfile.account
										&& gqlProfile.account.subscriptions
										&& gqlProfile.account.subscriptions.paid)
									|| [],
								currentTimestamp: gqlProfile && gqlProfile.currentTimestamp,
							})}
						</div>
					</div>
					<div
						className={`overlay ${!this.props.mainContainerOverlayEnabled ? 'not-visible' : 'visible'}`}
					/>
					{this.renderFeedbackSurvey()}
				</Provider>
				<Footer
					locale={lang}
					isAllAppsStarter={this.isAllAppsStarter()}
					isMobilePhotoPlan={this.isMobilePhotoPlan()}
					isNUJTestWorkflow={this.isNUJTestWorkflow()}
				/>
				{(lang === 'en' || window.cchome.util.hasFeature('temp_enable_ccinstall_toast_all'))
					&& this.showCCInstallationDialog()
					&& !lifelineEligible() && (
					<CCInstallationView
						profile={profile}
						imsData={AppsUtils.getIMSData()}
						environment={env}
						locale={lang}
					/>
				)}
			</div>
		);
	}
}

function mapStateToProps({app, profile}) {
	return {
		forceHideMenu: app.forceHideMenu,
		forceHideNavbar: app.forceHideNavbar,
		mainContainerOverlayEnabled: app.mainContainerOverlayEnabled,
		profileFetchError: profile.profileTimeoutOrError,
		// profileFetchFinished: profile.profileFetchFinished,
		gqlProfile: profile.gqlProfile,
		planCode: profile.subscriptionInfo.planCode,
		language: app.language,
	};
}

export default {
	component: requireAuth(
		injectIntl(connect(mapStateToProps, {setAnalyticsOptinInited, resetProfileFetchError})(App)),
	),
};
